import { type FC, useCallback, useEffect, useRef } from "react";
import { connect, type ConnectedProps } from "react-redux";
import { bindActionCreators } from "redux";

import { type GroupsOverviewProps } from "../types";
import { FilterType, type FiltersMap } from "../../../../../utils/filterUtils";
import { formatFilters } from "utils/filterMapUtils";
import { RolePermissions, type SortingDirection, ViewType } from "../../../../../enums";
import { type Group } from "../../../../../interfaces";
import { type Filters } from "../../../../../utils/queryUtils";
import { DEFAULT_REQUEST_PARAMS, DEFAULT_SORTING_COLUMN_NAME } from "../helpers/constants";
import GroupsTableBody from "../../../../../components/people/overview/groupsTable";
import { SearchInput } from "../../../../../components";
import { GroupFilterForm } from "../../../../../components/filterForms";
import { ItemsView } from "../../../../../views";
import { fetchGroups as fetchGroupsAction, type FetchGroupsRequest } from "../../state/actions/groupAssignmentActions";
import { type AppDispatch, type RootState } from "../../../../Application/globaltypes/redux";
import { fetchFilterOptions as fetchFilterOptionsAction } from "../../../../People/Groups/GroupsOverview/state/filterActions";
import * as groupFiltersActionsRedux from "../../state/slice/GroupAssignmentFilterSlice";

import sortDirections from "../../../../../enums/sortDirections";
import groupPermissionActions from "../../state/actions/groupPermissionActions";
import Restricted from "../../../../Application/Restricted";
import { getGroupsColumnOptions } from "components/people/overview/getGroupsColumnOptions";
import { isEmpty } from "lodash";

const sortChangeNameMap: Record<string, string> = {
  added: "DateCreated",
  name: "name",
} as const;

export type GroupsOverviewPropsAll = GroupsOverviewProps & PropsFormRedux;

export const GroupsOverview: FC<GroupsOverviewPropsAll> = (props: GroupsOverviewPropsAll): React.ReactElement => {
  const { fetchFilterOptions, fetchGroups, updateGroupsAssignment } = props;
  const { setAppliedFilter, resetAppliedFilter, setSearch, resetSearch } = props.groupFilterActions;

  const getFilterOptionsMemo = useCallback(() => fetchFilterOptions(), [fetchFilterOptions]);
  const onSelectedListItemsChangedMemo = useCallback(
    (groupIds: number[]) => updateGroupsAssignment(groupIds),
    [updateGroupsAssignment],
  );
  const applyFilterMemo = useCallback((filter: Filters) => setAppliedFilter(filter), [setAppliedFilter]);
  const resetFilterMemo = useCallback(() => resetAppliedFilter(), [resetAppliedFilter]);

  useEffect(
    () => () => {
      resetSearch();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const getGroups = (
    skip: number = DEFAULT_REQUEST_PARAMS.skip,
    top: number = DEFAULT_REQUEST_PARAMS.top,
    sortingColumnName: string = DEFAULT_REQUEST_PARAMS.sortingColumnName,
    sortingDirection: SortingDirection = DEFAULT_REQUEST_PARAMS.sortingDirection,
    appliedFilter: FiltersMap = DEFAULT_REQUEST_PARAMS.appliedFilter,
  ): void => {
    fetchGroups({
      skip,
      top,
      sortBy: sortChangeNameMap[sortingColumnName],
      sortOrder: sortingDirection,
      ...formatFilters(appliedFilter, { DateCreated: FilterType.DateRange }),
      term: props.search,
    });
  };

  const reloadListItems = useRef<(enableSorting: boolean) => void>();

  const createReloadListItems = (reloadListItemsFunc: (enableSorting: boolean) => void) => {
    reloadListItems.current = reloadListItemsFunc;
  };

  const onSearchChanged = (search: string): void => {
    setSearch(search);
    reloadListItems.current?.(isEmpty(search));
  };

  const buildTableBody = (group: Group): React.ReactElement => <GroupsTableBody group={group} />;
  const renderFilterForm = (): React.ReactElement => <GroupFilterForm />;
  const renderSearchInput = (): React.ReactElement => (
    <SearchInput placeholder="Search for People..." defaultValue={props.search} onChange={onSearchChanged} />
  );

  const renderPeopleList = () => {
    const {
      customHeaderContent,
      isReadOnly,
      assignedPeopleIds,
      isLoading,
      groupsCount,
      groupsToBeAssigned,
      filterOptions,
      appliedFilter,
    } = props;
    return (
      <Restricted
        permissions={[RolePermissions.GroupsView]}
        renderContent={(hasAnyPermission: boolean) => (
          <ItemsView
            viewType={ViewType.LIST}
            columnOptions={getGroupsColumnOptions()}
            getData={getGroups}
            itemsInlineCount={groupsCount}
            isLoading={isLoading}
            items={groupsToBeAssigned}
            buildTableBody={buildTableBody}
            customHeaderContent={customHeaderContent}
            renderSearch={renderSearchInput}
            sortingColumnName={DEFAULT_SORTING_COLUMN_NAME}
            sortingDirection={sortDirections.Desc}
            selectedIds={assignedPeopleIds}
            onSelectedListItemsChanged={onSelectedListItemsChangedMemo}
            filterOptionsLoading={filterOptions.isLoading}
            filterOptions={filterOptions}
            applyFilter={applyFilterMemo}
            resetFilter={resetFilterMemo}
            getFilterOptions={getFilterOptionsMemo}
            appliedFilter={appliedFilter}
            renderFilterForm={renderFilterForm}
            accessRestricted={!hasAnyPermission}
            isSelectDisabled={() => isReadOnly}
            setReloadListItems={createReloadListItems}
          />
        )}
      />
    );
  };

  return renderPeopleList();
};
const mapStateToProps = (state: RootState) => ({
  accountId: state.userProfile.accountId,
  groupsToBeAssigned: state.library.groupAssignment.groupsToBeAssigned.items,
  groupsCount: state.library.groupAssignment.groupsToBeAssigned.itemsCount,
  isLoading: state.library.groupAssignment.groupsToBeAssigned.isLoading,
  filterOptions: state.people.groupsOverview.filterOptions,
  appliedFilter: state.library.groupAssignment.filter.appliedFilter,
  search: state.library.groupAssignment.filter.search,
});

const mapDispatchToProps = (dispatch: AppDispatch, ownProps: GroupsOverviewProps) => ({
  fetchGroups: (request: FetchGroupsRequest) => dispatch(fetchGroupsAction(request)),
  fetchFilterOptions: () => dispatch(fetchFilterOptionsAction()),
  updateGroupsAssignment: (groupsId: number[]) => {
    dispatch(groupPermissionActions(ownProps.section).updateGroupsAssignment(groupsId));
  },
  groupFilterActions: bindActionCreators(groupFiltersActionsRedux, dispatch),
});

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFormRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(GroupsOverview);
export default ConnectedComponent;
